import {Banner} from '@primer/react'
import {useState} from 'react'

import {BUTTON_LABELS, LABELS} from '../../constants'

interface SavedViewMutationErrorBannerProps {
  message: string | null
  onRetry?: () => void
  isRetrying?: boolean
  /**
   * Whether Try again is unavailable. It stays focusable and does nothing when activated.
   * @default false
   */
  retryDisabled?: boolean
  flush?: boolean
  focusOnMount?: boolean
}

function focusBannerOnMount(node: HTMLElement | null) {
  if (node) node.focus({preventScroll: true})
}

export function SavedViewMutationErrorBanner({
  message,
  onRetry,
  isRetrying = false,
  retryDisabled = false,
  flush,
  focusOnMount = true,
}: SavedViewMutationErrorBannerProps) {
  // Keep the last message visible while a retry is in flight so focus stays
  // on "Try again".
  const [stickyMessage, setStickyMessage] = useState<string | null>(message)
  const nextMessage = message ?? (isRetrying ? stickyMessage : null)
  if (nextMessage !== stickyMessage) setStickyMessage(nextMessage)

  if (!stickyMessage) return null

  return (
    <Banner
      ref={focusOnMount ? focusBannerOnMount : undefined}
      variant="critical"
      title={stickyMessage}
      flush={flush}
      actionsLayout={onRetry ? 'inline' : undefined}
      primaryAction={
        onRetry ? (
          <Banner.PrimaryAction
            onClick={retryDisabled ? undefined : onRetry}
            // Not `disabled`: a focused button that turns disabled drops focus to the page.
            {...(retryDisabled ? {'aria-disabled': true} : {})}
            loading={isRetrying}
            loadingAnnouncement={LABELS.views.retrying}
          >
            {BUTTON_LABELS.tryAgain}
          </Banner.PrimaryAction>
        ) : undefined
      }
    />
  )
}
